<template>
<div>
  <!--    Loader-->
  <loading-panel :loading="loading"></loading-panel>



  <div class="row mt-2">
    <div class="col" >
      <card card-body-classes="table-full-width" class="no-scrollbar" style="overflow-x: scroll">
        <!-- Title with the date bar anchored right; the date bar hides while the
             Incomplete filter lists every date, and wraps under the title on phones -->
        <div class="tracker-title-row">
          <h4 v-if="!incomplete_view" class="card-title mb-0">Events for <span class="text-primary"> {{getFormattedDate(current)}}</span></h4>
          <h4 v-else class="card-title mb-0">All incomplete forms</h4>
          <template v-if="!incomplete_view">
            <base-date-navigator class="tracker-title-row__nav d-sm-none" :value="current" @change="onNavigateDate"></base-date-navigator>
            <base-date-navigator class="tracker-title-row__nav d-none d-sm-flex" :value="current" fit-content @change="onNavigateDate"></base-date-navigator>
          </template>
        </div>
        <filter-bar
          :filters="filterSchema"
          :initial-value="filterValues"
          saved-filters-key="game-tracker"
          mobile-collapsible
          @change="onFiltersChanged"
        ></filter-bar>
        <div class="mt-3" v-if="game_instances.length <= 0">
          <div class="font-icon-detail">
              <span style="font-size: 3em">
                <i class="fas fa-exclamation-triangle"></i>
              </span>
            <h4 class="mt-3" v-if="incomplete_view && filterValues.game.length === 0 && filterValues.staff.length === 0">No incomplete forms.</h4>
            <h4 class="mt-3" v-else-if="filterValues.game.length === 0 && filterValues.staff.length === 0">No upcoming bookings today.</h4>
            <h4 class="mt-3" v-else>No bookings match the selected filter.</h4>
          </div>
        
</div>
<!--        Mobile and tablet: cards until the table has room (xl) -->
        <div v-if="game_instances.length > 0" class="row" :class="incomplete_view ? 'tracker-cards--incomplete' : 'd-xl-none'">
          <div class="col">
            <b-table hover :items="game_instances"
                     :fields="mobileFields" thead-class="d-none">
              <template v-slot:cell(game)="row">
                <div class="row justify-content-between">
                  <div class="col">
                    <p class="text-primary" style="font-size: medium; margin-bottom: 0;">{{row.item.game_name}}</p>
                    <small v-if="row.item.staff && parseJSON(row.item.staff).length > 0" style="opacity: 0.7; font-size: 11px; display: block; margin-bottom: 8px;">
                      <i class="tim-icons icon-single-02"></i> {{ formatStaffList(row.item.staff) }}
                    </small>
                  </div>
                  <div class="col text-right" v-if="row.item.players">
                    <el-popover trigger="hover"
                                placement="bottom">
                      <div>
                        <div class="popover-body">
                          <div v-for="(p, i) in parseJSON(row.item.players)" :key="'C' + i">
                            {{p.first_name}} {{p.last_name}}
                          </div>
                        </div>
                      </div>
                      <base-button class="like btn-link" v-on:mouseenter="getWaivers(row.item)"
                                   type="info"
                                   size="sm"
                                   icon
                                   slot="reference">
                        <span class="text-white">x{{row.item.waiver_count}}</span>
                      </base-button>
                    </el-popover>
                  </div>
                  <div class="col text-right" v-else>
                    <base-button class="like btn-link"
                                 type="info"
                                 size="sm"
                                 icon
                                 slot="reference">
                      <span class="text-white">x{{row.item.waiver_count}}</span>
                    </base-button>
                  </div>
                </div>
                <div class="row">
                  <div class="col">
                    <i class="text-primary tim-icons icon-calendar-60"></i> {{getFormattedDate(row.item.booking_date)}}
                    <i class="ml-2 text-primary tim-icons icon-time-alarm"></i> {{getTimeFromTime(row.item.start_time)}}
                  </div>
                </div>
                <!-- Fields flow into as many columns as the card width allows:
                     two on a phone, four or five on a tablet -->
                <div class="tracker-card-grid mt-3">
                  <base-input label="Start time" :error="errors.start_time" >
                    <el-time-picker v-on:input="validateTime(row.item.game_start_time, current)"
                      v-model="row.item.game_start_time"
                      :format="picker_time_format" placeholder="HH:MM"
                    ></el-time-picker>
                  </base-input>
                  <base-input label="End time" :error="errors.end_time">
                    <el-time-picker placeholder="HH:MM" v-on:input="validateTime(row.item.game_end_time, current)"
                                    v-model="row.item.game_end_time"
                                    :format="picker_time_format"
                    ></el-time-picker>
                  </base-input>
                  <base-input v-if="enabledFields.custom_time" class="custom-time-input" label="Custom time" placeholder="MM:SS" inputmode="numeric" maxlength="8" :value="row.item.custom_time" @input="val => onRowCustomTimeInput(row.item, val)" @blur="onRowCustomTimeBlur(row.item)"></base-input>
                  <base-input v-if="enabledFields.hints" label="Number of hints" placeholder="0"
                              :error="errors.hints"  v-model="row.item.hints" type="number" ></base-input>
                  <base-input v-if="enabledFields.breaks" label="Number of breaks" :error="errors.breaks" v-model="row.item.breaks"
                              placeholder="0" type="number" ></base-input>
                  <base-input v-if="enabledFields.team_name" class="tracker-card-grid__wide" label="Team name" placeholder="Team name" v-model="row.item.team_name"></base-input>
                  <base-input v-if="enabledFields.host" class="tracker-card-grid__wide" label="Host">
                    <el-select v-model="row.item.host_id" class="select-primary" placeholder="Host">
                      <el-option v-for="s in staffMembers" :key="'MH' + s.id" class="select-primary"
                                 :label="getStaffDisplayName(s)" :value="s.id"></el-option>
                    </el-select>
                  </base-input>
                </div>
                <div class="tracker-card-toggles" v-if="enabledFields.corporate_booking || enabledFields.completed">
                  <div v-if="enabledFields.corporate_booking">
                    <el-switch active-color="#e14eca"
                                v-model="row.item.is_corporate_booking" />
                    <label> &nbsp; Corporate booking</label>
                  </div>
                  <div v-if="enabledFields.completed">
                    <el-switch active-color="#e14eca"
                               v-model="row.item.completed" />
                    <label> &nbsp; Event was completed</label>
                  </div>
                </div>
                <div class="tracker-card-footer">
                <div class="mt-2 mb-2" v-if="enabledFields.photo">
                  <label style="font-size: 12px; opacity: 0.7; margin-bottom: 6px; display: block;">Photos</label>
                  <div class="photo-thumbnails-row">
                    <div v-for="(photo, pIdx) in (row.item.photos || [])" :key="'mb-photo-' + pIdx + '-' + img_key"
                         class="photo-thumb-wrapper" @click="openLightbox(row.item, pIdx)">
                      <div v-if="photo.isProcessingOverlay" class="photo-thumb-overlay">
                        <i class="fas fa-spinner fa-spin" style="font-size: 1.2em;"></i>
                      </div>
                      <img height="50" width="50" style="object-fit: cover; border-radius: 4px; cursor: pointer;"
                           :src="photo.overlay_url || photo.uploaded_image"/>
                    </div>
                    <span v-if="!photos_not_enabled" class="photo-add-tile btn-file" @click="onTileClick(row.item)">
                      <i class="tim-icons icon-simple-add"></i>
                      <input v-if="!useLiveCamera && isAndroid14" type="file" @change="takePhoto(row.item, $event)"/>
                      <input v-else-if="!useLiveCamera" type="file" accept="image/*" @change="takePhoto(row.item, $event)"/>
                    </span>
                  </div>
                  <div class="mt-2" v-if="row.item.photos && row.item.photos.length > 0">
                    <el-switch active-color="#e14eca" v-model="row.item.email_to_group" @change="onEmailToGroupToggle(row.item)" />
                    <label> &nbsp; Email to group</label>
                  </div>
                </div>
                <base-button class="tracker-card-submit"
                             @click.native="submitForm(row.item)"
                             type="primary"
                             size="sm"
                ><i class="tim-icons icon-check-2 mr-1"></i>Submit
                </base-button>
                </div>
              </template>
            </b-table>
          </div>
        </div>
<!--        Desktop-->
        <div v-if="game_instances.length > 0" class="row" :class="incomplete_view ? 'tracker-table--incomplete' : 'd-none d-xl-block'">
          <div class="col tracker-fit">
            <b-table hover responsive :items="game_instances"
                     :fields="fields">
              <template #head()="data">{{ shortHeader(data) }}</template>
              <template v-slot:cell(game)="row">
                <div class="tracker-fit__event">
                  <span class="text-primary tracker-fit__event-name" :title="row.item.game_name">{{row.item.game_name}}</span>
                  <small v-if="row.item.staff && parseJSON(row.item.staff).length > 0" class="tracker-fit__staff" :title="formatStaffList(row.item.staff)">
                    <i class="tim-icons icon-single-02"></i> {{ formatStaffList(row.item.staff) }}
                  </small>
                </div>
              </template>
              <template v-slot:cell(photo)="row">
                <div>
                <div class="photo-thumbnails-row tracker-fit__photos">
                  <div v-for="(photo, pIdx) in (row.item.photos || []).slice(0, 2)" :key="'dt-photo-' + pIdx + '-' + img_key"
                       class="photo-thumb-wrapper" @click="openLightbox(row.item, pIdx)">
                    <div v-if="photo.isProcessingOverlay" class="photo-thumb-overlay">
                      <i class="fas fa-spinner fa-spin" style="font-size: 1.2em;"></i>
                    </div>
                    <img height="50" width="50" style="object-fit: cover; border-radius: 4px; cursor: pointer;"
                         :src="photo.overlay_url || photo.uploaded_image"/>
                  </div>
                  <span v-if="row.item.photos && row.item.photos.length > 2" class="photo-add-tile tracker-fit__more"
                        @click="openLightbox(row.item, 2)">+{{ row.item.photos.length - 2 }}</span>
                  <el-tooltip content="Add photo" effect="light" :open-delay="300" placement="top">
                    <span v-if="!photos_not_enabled" class="photo-add-tile btn-file" @click="onTileClick(row.item)">
                      <i class="tim-icons icon-simple-add"></i>
                      <input v-if="!useLiveCamera && isAndroid14" type="file" @change="takePhoto(row.item, $event)"/>
                      <input v-else-if="!useLiveCamera" type="file" accept="image/*" @change="takePhoto(row.item, $event)"/>
                    </span>
                  </el-tooltip>
                </div>
                <div class="mt-2" v-if="row.item.photos && row.item.photos.length > 0">
                  <el-switch active-color="#e14eca" v-model="row.item.email_to_group" @change="onEmailToGroupToggle(row.item)" />
                  <label> &nbsp; Email to group</label>
                </div>
                </div>
              </template>
              <template v-slot:cell(booking_time)="row">
                <div class="tracker-fit__w-booking">
                  <div>{{getFormattedDate(row.item.booking_date)}}</div>
                  <div class="tracker-fit__booking-time">{{getTimeFromTime(row.item.start_time)}}</div>
                </div>

              </template>
              <template v-slot:cell(time)="row">
                <div style="white-space: nowrap">
                    <span>{{getTimeFromTime(row.item.start_time)}}</span>
                </div>

              </template>
              <template v-slot:cell(start_time)="row">
                <div class="tracker-fit__w-time">
                  <base-input :error="errors.start_time" >
                    <el-time-picker v-on:input="validateTime(row.item.game_start_time, current)"
                      v-model="row.item.game_start_time"
                      :format="picker_time_format" placeholder="HH:MM"
                    ></el-time-picker>
                  </base-input>
                </div>

              </template>
              <template v-slot:cell(end_time)="row">
                <div class="tracker-fit__w-time">
                  <base-input :error="errors.end_time">
                    <el-time-picker placeholder="HH:MM" v-on:input="validateTime(row.item.game_end_time, current)"
                      v-model="row.item.game_end_time"
                      :format="picker_time_format"
                    ></el-time-picker>
                  </base-input>
                </div>

              </template>
              <template v-slot:cell(players)="row">
                <div v-if="row.item.players">
                  <el-popover trigger="hover"
                              placement="bottom">
                    <div >
                      <div class="popover-body" >
                        <div v-for="(p, i) in parseJSON(row.item.players)" :key="'C' + i">
                          {{p.first_name}} {{p.last_name}}
                        </div>
                      </div>
                    </div>
                    <base-button class="like btn-link"
                                 type="info"
                                 size="sm"
                                 icon
                                 slot="reference">
                      <span class="text-white">x{{row.item.waiver_count}}</span>
                    </base-button>
                  </el-popover>

                </div>
                <div v-else>
                  <base-button class="like btn-link"
                               type="info"
                               size="sm"
                               icon
                               slot="reference">
                    <span class="text-white">x{{row.item.waiver_count}}</span>
                  </base-button>
                </div>
              </template>
              <template v-slot:cell(team)="row">
                <div class="tracker-fit__w-team">
                  <base-input placeholder="Team name" v-model="row.item.team_name"></base-input>
                </div>
              </template>
              <template v-slot:cell(#_of_hints)="row">
                <div class="tracker-fit__w-count">
                  <base-input placeholder="0"
                    :error="errors.hints"  v-model="row.item.hints" type="number" ></base-input>

                </div>
              </template>
              <template v-slot:cell(#_of_breaks)="row">
                <div class="tracker-fit__w-count">
                  <base-input :error="errors.breaks" v-model="row.item.breaks"
                              placeholder="0" type="number" ></base-input>

                </div>

              </template>
              <template v-slot:cell(completed)="row">
                <el-switch active-color="#e14eca"
                           v-model="row.item.completed" />
              </template>
              <template v-slot:cell(corporate_booking)="row">
                <el-switch active-color="#e14eca"
                           v-model="row.item.is_corporate_booking" />
              </template>
              <template v-slot:cell(custom_time)="row">
                <div class="tracker-fit__w-custom">
                  <base-input class="custom-time-input" placeholder="MM:SS" inputmode="numeric" maxlength="8" :value="row.item.custom_time" @input="val => onRowCustomTimeInput(row.item, val)" @blur="onRowCustomTimeBlur(row.item)"></base-input>
                </div>
              </template>
              <template v-slot:cell(host)="row">
                <div class="tracker-fit__w-host">
                  <base-input>
                    <el-select v-model="row.item.host_id" class="select-primary" placeholder="Host">
                      <el-option v-for="s in staffMembers" :key="'H' + s.id" class="select-primary"
                                 :label="getStaffDisplayName(s)" :value="s.id"></el-option>
                    </el-select>
                  </base-input>
                </div>
              </template>
              <template v-slot:cell(submit)="row">


                <div>
<!--                  <el-tooltip content="Take photo"-->
<!--                              effect="light"-->
<!--                              :open-delay="300"-->
<!--                              placement="top">-->
<!--                  <span v-if="!photos_not_enabled" class="btn btn-success btn-link btn-file mt-3" style="display: inline-block; font-size: large">-->
<!--                    <i class="tim-icons icon-camera-18"></i>-->
<!--                      <input v-if="isAndroid14" type="file"-->
<!--                           @change="takePhoto(row.item, $event)"/>-->
<!--                      <input v-else type="file"-->
<!--                             accept="image/*"-->
<!--                             @change="takePhoto(row.item, $event)"-->

<!--                      />-->
<!--                  </span>-->
<!--                  </el-tooltip>-->
                  <el-tooltip content="Submit form"
                              effect="light"
                              :open-delay="300"
                              placement="top">
                  <span class="btn btn-primary btn-link btn-file m-0 tracker-fit__submit" v-on:click="submitForm(row.item)">
                    <i class="tim-icons icon-check-2"></i>
                  </span>
                  </el-tooltip>
                </div>

              </template>

            </b-table>
          </div>
        </div>
      </card>

    </div>
  </div>

  <base-alert-dialog :show="alertDialog.show"
                     :title="alertDialog.title"
                     :message="alertDialog.message"
                     :variant="alertDialog.variant"
                     :show-cancel="alertDialog.showCancel"
                     :confirm-text="alertDialog.confirmText"
                     :cancel-text="alertDialog.cancelText"
                     :confirm-variant="alertDialog.confirmVariant"
                     @confirm="resolveAlert(true)"
                     @cancel="resolveAlert(false)" />

  <!-- Photo drawer: browse the booking's photos, pick or clear an overlay, adjust and apply. -->
  <adjust-overlay-drawer
    :show="showLightbox"
    :photos="lightboxItem ? lightboxItem.photos : null"
    :photo-index="lightboxPhotoIndex"
    :overlays="overlays"
    :resolve-text="content => lightboxItem ? processDynamicContent(content, lightboxItemData) : content"
    @close="closeLightbox"
    @delete="onLightboxDelete"
    @clear-overlay="onLightboxClearOverlay"
    @apply="onLightboxApply"
    @update:photoIndex="lightboxPhotoIndex = $event" />
  <adjust-overlay-drawer
    :show="!!pendingPhoto"
    pending
    :photos="pendingPhoto ? [{ uploaded_image: pendingPhoto.url, overlay_id: pendingPhoto.overlayId, overlay_layers: null }] : null"
    :photo-index="0"
    :overlays="overlays"
    :resolve-text="content => pendingPhoto ? processDynamicContent(content, { ...pendingPhoto.item, game_name: eventNameFor(pendingPhoto.item) }) : content"
    @close="cancelPending"
    @retake="retakePending"
    @use="usePending" />
  <live-camera-capture
    :show="liveCamera.show"
    :overlays="overlays"
    :overlay-id="liveCamera.overlayId"
    :resolve-text="content => liveCamera.item ? processDynamicContent(content, { ...liveCamera.item, game_name: eventNameFor(liveCamera.item) }) : content"
    @close="closeLiveCamera"
    @use-native="useNativeCamera"
    @capture="onLiveCapture" />
  <input ref="nativeInput" type="file" :accept="isAndroid14 ? undefined : 'image/*'" style="display: none" @change="takePhoto(liveCamera.item, $event)">
</div>
</template>

<script>
import {Auth} from "aws-amplify";
import {API_LOCATION} from "@/api-config";
import {groupTimeDigits, formatCustomTime, toStoredCustomTime} from "@/util/customTime";
import { defaultHostId } from "@/util/trackerHost";
import axios from "axios";
import { Badge } from 'src/components';
import { BTable } from 'bootstrap-vue'
import {dayOfWeek, formattedDate, time} from "@/plugins/dateFormatter";
import {Dropdown, DropdownItem, DropdownMenu, Popover, Switch, TimePicker} from 'element-ui';
import _ from "lodash";
import { bakeOverlay, preparePhotoCanvas } from '@/util/overlayRenderer';
import { effectiveLayers, cropsToOverlay } from '@/util/overlayGeometry';
import AdjustOverlayDrawer from "@/components/Photos/AdjustOverlayDrawer.vue";
import LiveCameraCapture from "@/components/Photos/LiveCameraCapture.vue";

// JPEG quality for the auto-applied overlay bake. Quality 1 disables
// quantization and produced composites 3 to 4 times larger than the phone
// original, which then failed to email. 0.95 is visually identical.
const OVERLAY_EXPORT_QUALITY = 0.95


const NO_RECIPIENTS_MESSAGE = 'Photos are emailed to the players on this booking who signed a waiver with an email address, and none of them has one. Add an email to a player, or assign the photo to a customer on the Photos page and send it from there.'
// True when the photo email endpoint rejected the send because nobody on the
// booking has an email address (400 with error 'No valid recipients').
function isNoRecipientsError(err) {
  if (!err || !err.response || err.response.status !== 400) return false
  let data = err.response.data
  if (typeof data === 'string') { try { data = JSON.parse(data) } catch (e) { return false } }
  return !!(data && data.error === 'No valid recipients')
}

export default {
  name: "GameTrackerSelect",
  components:{
    BTable,
    Badge,
    AdjustOverlayDrawer,
    LiveCameraCapture,
    [TimePicker.name]: TimePicker,
    [Popover.name]: Popover,
    [Switch.name]: Switch,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem
  },
  data(){
    return {
      alertDialog: { show: false, title: '', message: '', variant: 'warning', showCancel: false, confirmText: 'OK', cancelText: 'Cancel', confirmVariant: 'primary' },
      alertResolve: null,
      photos_not_enabled: '',
      img_key: 0,
      overlays: [],
      // Review before saving (Photo settings toggle): the taken file waits here until Use photo.
      review_before_save: false,
      pendingPhoto: null,
      // Camera choice (Photo settings): 'native' opens the device camera, 'live' the in page camera view.
      camera_mode: 'native',
      liveCamera: { show: false, item: null, overlayId: null },
      showOverlayModal: false,
      selectedItem: null,
      selectedPhoto: null,
      modalSelectedOverlay: '',
      isProcessingOverlay: false,
      previewDataUrl: '',
      showLightbox: false,
      lightboxItem: null,
      lightboxPhotoIndex: 0,
      date_format: JSON.parse(localStorage.getItem('group')).date_format,
      time_format: JSON.parse(localStorage.getItem('group')).time_format,
      first_day_of_week: parseInt(JSON.parse(localStorage.getItem('group')).first_day_of_week) || 1,
      date_format_cal: '',
      day_of_week: '',
      picker_time_format: 'hh:mm A',
      start_time: '',
      end_time: '',
      incomplete_view: false,
      hints: 0,
      breaks: 0,
      completed: '',
      completionOptions: [
        {
          id: 0,
          name: 'Completed'
        },
        {
          id: 1,
          name: 'Not completed'
        }
      ],
      errors: {
        hints: '',
        breaks: '',
        completed: '',
        start_time: '',
        end_time: ''
      },
      enabledFields: {
        hints: true,
        breaks: true,
        completed: true,
        corporate_booking: true,
        custom_time: false,
        photo: true,
        team_name: true,
        host: false,
        puzzles: false,
        pre_game_notes: false,
        post_game_notes: false,
        team_experience: false
      },
      loading: false,
      current: '',
      template: {},
      userAgent: navigator.userAgent,
      game_instances: [],
      mobileFields: [{ key: 'game', label: 'Event' }],
      fields: ['time', { key: 'game', label: 'Event' }, 'players', 'start_time', 'end_time'],
      games: [],
      staffMembers: [],
      // Flat filter values for the shared <filter-bar>. game = array of game ids,
      // staff = array of staff ids (may include the 'unassigned' sentinel). Seeded
      // from the URL. The date (`current`) is navigation, NOT a saved filter, so it
      // lives in its own state and never enters filterValues or a saved view.
      filterValues: {
        game: [],
        staff: [],
        incomplete: false
      },
      // FilterBar (saved views) emits one initial `change` on mount; these guards keep
      // its first fetch from racing / double-running with getDefaultTracker's load.
      templateReady: false,
      filtersReady: false,
      didInitialFetch: false,
    }
  },
  computed: {

    // The photo drawer resolves [Event Name] from item.game_name, so hand it the customer facing name.
    lightboxItemData() {
      if (!this.lightboxItem) return null
      return { ...this.lightboxItem, game_name: this.eventNameFor(this.lightboxItem) }
    },
    isAndroid14() {
      return navigator.userAgent.match(/Android/i) !== null;
    },
    liveCameraSupported() {
      return typeof navigator !== 'undefined' && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia) && window.isSecureContext
    },
    useLiveCamera() {
      return this.camera_mode === 'live' && this.liveCameraSupported
    },
    // Schema for the shared <filter-bar>: event + staff multi-selects.
    filterSchema() {
      return [
        { key: 'game', type: 'multiselect', label: 'Event', primary: true,
          options: (this.games || []).map(g => ({ label: g.name, value: g.id })) },
        { key: 'staff', type: 'multiselect', label: 'Staff', primary: true,
          options: [
            { label: 'Unassigned', value: 'unassigned' },
            ...(this.staffMembers || []).map(s => ({ label: this.getStaffDisplayName(s), value: s.id }))
          ] },
        // Incomplete forms span every date, so the date bar hides while this is on
        ...(this.hasPermissions('game_history_update')
          ? [{ key: 'incomplete', type: 'toggle', label: 'Incomplete', primary: true }]
          : [])
      ]
    }
  },
  methods: {
    // Promise-based wrapper around the shared base-alert-dialog. Resolves true
    // on confirm, false on cancel or dismiss.
    showAlert(opts){
      if(this.alertResolve){
        this.alertResolve(false)
      }
      this.alertDialog = {
        show: true,
        title: opts.title || '',
        message: opts.message || '',
        variant: opts.variant || 'warning',
        showCancel: opts.showCancel === true,
        confirmText: opts.confirmText || 'OK',
        cancelText: opts.cancelText || 'Cancel',
        confirmVariant: opts.confirmVariant || (opts.showCancel && opts.variant === 'warning' ? 'danger' : 'primary')
      }
      return new Promise(resolve => { this.alertResolve = resolve })
    },
    resolveAlert(value){
      this.alertDialog.show = false
      const resolve = this.alertResolve
      this.alertResolve = null
      if(resolve) resolve(value)
    },
    onRowCustomTimeInput(item, val){
      // hold the grouped display value on the row; convert to canonical HH:MM:SS at submit
      this.$set(item, 'custom_time', groupTimeDigits(val))
    },
    onRowCustomTimeBlur(item){
      // canonicalize first: the row holds the grouped display value, and short
      // colon-less entries ("10") would otherwise format to '' and wipe the field
      this.$set(item, 'custom_time', formatCustomTime(toStoredCustomTime(item.custom_time)))
    },
    async getGames() {
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/games'

      axios.get(url, config)
        .then(response => {
          this.games = response.data
        })
        .catch(err => {
          console.log('Failed to load games:', err)
        })
    },
    async getStaffMembers() {
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/company-users'

      axios.get(url, config)
        .then(response => {
          this.staffMembers = response.data.company_users
          this.applyHostDefaults()
        })
        .catch(err => {
          console.log('Failed to load staff members:', err)
        })
    },
    // FilterBar change: apply new values and (re)fetch. The first change is
    // FilterBar's initial saved-views resolution; the guards defer the actual first
    // fetch to getDefaultTracker so we don't double-run.
    onFiltersChanged(values) {
      this.filterValues = {
        game: Array.isArray(values.game) ? values.game : [],
        staff: Array.isArray(values.staff) ? values.staff : [],
        incomplete: values.incomplete === true
      }
      if (!this.didInitialFetch) {
        this.filtersReady = true
        this.maybeInitialFetch()
        return
      }
      this.updateQueryParameters()
      this.fetchForCurrentView()
    },
    fetchForCurrentView() {
      if (this.filterValues.incomplete === true) {
        this.getIncompleteForms()
      } else {
        this.getTodaysGames()
      }
    },
    // Runs the first fetch once BOTH the tracker template (mode/redirect + fields) and
    // FilterBar's resolved filters are ready. Idempotent.
    maybeInitialFetch() {
      if (this.didInitialFetch) return
      if (this.templateReady && this.filtersReady) {
        this.didInitialFetch = true
        this.fetchForCurrentView()
      }
    },
    updateQueryParameters() {
      const newQuery = {
        ...this.$route.query,
        game_id: this.filterValues.game.length > 0 ? this.filterValues.game.join(',') : undefined,
        staff_id: this.filterValues.staff.length > 0 ? this.filterValues.staff.join(',') : undefined,
        incomplete: this.filterValues.incomplete === true ? '1' : undefined,
        day: this.current || undefined
      }
      // Remove undefined values
      Object.keys(newQuery).forEach(key => newQuery[key] === undefined && delete newQuery[key])
      this.$router.replace({ query: newQuery }).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          throw err
        }
      })
    },
    initFromQueryParameters() {
      // Seed FilterBar's flat values from the URL (game ids, staff ids). FilterBar
      // resolves saved-views precedence over these on mount.
      if (this.$route.query.game_id) {
        this.filterValues.game = this.$route.query.game_id.split(',').map(id => parseInt(id))
      }
      if (this.$route.query.staff_id) {
        this.filterValues.staff = this.$route.query.staff_id.split(',').map(id => {
          return id === 'unassigned' ? 'unassigned' : parseInt(id)
        })
      }
      if (this.$route.query.incomplete === '1') {
        this.filterValues.incomplete = true
      }
      // The date (`current`) is navigation, not a saved filter.
      if (this.$route.query.day) {
        this.current = this.$route.query.day
      }
    },
    onNavigateDate(value) {
      this.current = value
      this.onDateChange()
    },
    onDateChange() {
      this.updateQueryParameters()
      this.getTodaysGames()
    },
    getStaffDisplayName(staff) {
      if (!staff.first_name && !staff.last_name) {
        return staff.email || 'Unknown'
      }
      return [staff.first_name, staff.last_name].filter(Boolean).join(' ')
    },
    formatStaffList(staffData) {
      if (!staffData) return ''
      const staff = typeof staffData === 'string' ? JSON.parse(staffData) : staffData
      if (!staff || staff.length === 0) return ''
      return staff.map(s => this.getStaffDisplayName(s)).join(', ')
    },
    async getUsersForBooking(row){
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/'
        + companyGroupId + '/bookings/' + row.id + '/waivers'

      axios.get(url, config)
        .then(response => {
          row.waiver_count = response.data.users.length
          row.players = JSON.stringify(response.data.users)
        }).catch((err) => {
        console.log(err)
        this.loading = false
        if(err.response.status == 401){
          this.showAlert({
            title: 'Error',
            message: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
            variant: 'danger'
          }).then((response)=> {
            this.$router.push('/')
          })
        }
        else if(err.response.status == 500){
          this.showAlert({
            title: 'Error',
            message: 'The feature has not been enabled as part of your subscription plan.',
            variant: 'danger'
          }).then((response)=> {
            this.$router.push('/')
          })
        }
        else {
          this.showAlert({ title: 'Error', message: 'Something went wrong', variant: 'danger' })
        }
      })
    },
    validateTime(time, day){
      let today = new Date(day)
      time.setMonth(today.getMonth(), today.getDate())
    },
    getDayOfWeek(d){
      if(JSON.parse(localStorage.getItem('group')).show_day_of_week){
        return dayOfWeek(d) + ', '
      }
      return ''
    },
    getFormattedDate(d){
      return this.getDayOfWeek(d) + formattedDate(this.date_format, d)
    },
    setToday(){
      let current = new Date()
      this.current = current.getFullYear() + '-' +  ('0' + (current.getMonth()+1)).slice(-2) + '-' + ('0' +current.getDate()).slice(-2)
    },
    hasPermissions(scope){
      let permissions = JSON.parse(localStorage.getItem('permissions'))
      const matchFound = permissions.some(element => element === scope);
      return matchFound
    },
    parseJSON(item){
      return JSON.parse(item)
    },
    // Short desktop column headers so the tracker row fits on one line
    shortHeader(data) {
      const labels = {
        booking_time: 'Booking',
        players: 'Players',
        start_time: 'Start',
        end_time: 'End',
        corporate_booking: 'Corporate',
        custom_time: 'Custom time',
        '#_of_hints': 'Hints',
        '#_of_breaks': 'Breaks',
        team: 'Team',
        submit: ''
      }
      return Object.prototype.hasOwnProperty.call(labels, data.column) ? labels[data.column] : data.label
    },
    applyHostDefaults() {
      if (!this.enabledFields.host || this.staffMembers.length === 0) return
      const profile = JSON.parse(localStorage.getItem('profile') || 'null')
      const currentUserId = profile ? profile.id : null
      for (const row of this.game_instances) {
        if (row.host_id === null) {
          row.host_id = defaultHostId(row.staff ? this.parseJSON(row.staff) : [], this.staffMembers, currentUserId)
        }
      }
    },
    getTimeFromTime(d) {
      return time(this.time_format, d);
    },
    getAuthToken() {
      return new Promise(function (resolve) {
        Auth.currentSession()
          .then(data => {
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    getLocalTime(d){
      return this.current.split('-')[0] +
        "-" + this.current.split('-')[1] + "-" + this.current.split('-')[2] +
        'T' + d.getHours() + ':' + d.getMinutes() + ':00.000Z'
    },
    async onEmailToGroupToggle(item){
      // When enabling group email, warn (allow override) if any recipient opted out of photo sharing.
      if(!item.email_to_group){
        return
      }
      let recipients = item.players ? JSON.parse(item.players) : []
      if(!(await this.confirmPhotoOptIn(recipients))){
        item.email_to_group = false
      }
    },
    async confirmPhotoOptIn(recipients){
      // Warn (allow override) if any recipient opted out of photo sharing.
      let list = recipients || []
      let optedOut = list.filter(u => u && u.opted_out_photo_sharing)
      if(optedOut.length === 0){
        return true
      }
      let isSingle = list.length === 1
      let names = optedOut
        .map(u => `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email)
        .join(', ')
      let confirmed = await this.showAlert({
        title: isSingle ? 'Customer opted out' : 'Some participants opted out',
        message: isSingle
          ? `This customer opted out of having their photo shared. Only send if you have their consent.`
          : `${names} opted out of having their photo shared. Only send if you have their consent.`,
        variant: 'warning',
        showCancel: true,
        confirmText: 'Send anyway',
        cancelText: 'Cancel'
      })
      return confirmed === true
    },
    async emailToGroup(photos, players){
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/photos/'+ photos[0].photo_id + '/email-group'

      let data = {
        photoIds: photos.map(p => p.photo_id),
        link_ids: photos.map(p => p.photo_link_id).filter(id => id != null),
        emails: JSON.parse(players),
        link_type: 1,
        link_id: photos[0].photo_link_id,
        // Auto-send on submit: never block on size, attach what fits and link the rest.
        allow_links: true
      }

      axios.post(url, data, config)
        .then(response => {

        }).catch((err) => {
        console.log(err)
        this.loading = false
        if(err.response.status == 401){
          this.$router.push('/forbidden')
        } else if(isNoRecipientsError(err)){
          this.showAlert({ title: 'No one to email', message: NO_RECIPIENTS_MESSAGE, variant: 'warning', confirmText: 'OK' })
        } else if(err.response.status == 413){
          let errorData = err.response.data
          if(typeof errorData === 'string'){
            try { errorData = JSON.parse(errorData) } catch(e) { errorData = {} }
          }
          let sizeText = errorData.total_mb ? ` are ${errorData.total_mb} MB after email encoding, which exceeds` : ' exceed'
          this.showAlert({
            title: 'Photos too large to email',
            message: `The selected photos${sizeText} the ${errorData.limit_mb || 40} MB email limit. Select fewer photos and send them in separate emails.`,
            variant: 'danger',
            confirmText: 'OK'
          })
        } else {
          this.showAlert({
            title: 'Something went wrong!',
            variant: 'danger',
            confirmText: 'Try again'
          })
        }

      })
    },
    async submitForm(item){
      console.log(item)
      let errors = 0
      if(!item.hasOwnProperty('game_start_time')){
        this.errors.start_time = "Must not be empty"
        errors++
      } else {
        this.errors.start_time = ""
      }
      if(!item.hasOwnProperty('game_end_time')){
        this.errors.end_time = "Must not be empty"
        errors++
      } else {
        this.errors.end_time = ""
      }
      if(this.enabledFields.hints) {
        if(!item.hasOwnProperty('hints') || item.hints === ""){
          errors++
          this.errors.hints = "Must not be empty"
        } else if (item.hints < 0){
          errors++
          this.errors.hints = "Invalid amount"
        } else {
          this.errors.hints = ""
        }
      }

      if(this.enabledFields.breaks) {
        if(!item.hasOwnProperty('breaks') || item.breaks === ""){
          errors++
          this.errors.breaks = "Must not be empty"
        } else if (item.breaks < 0){
          errors++
          this.errors.breaks = "Invalid amount"
        } else {
          this.errors.breaks = ""
        }
      }

      if(this.enabledFields.completed) {
        if(!item.hasOwnProperty('completed')){
          this.errors.completed = "Must not be empty"
          errors++
        } else {
          this.errors.completed = ""
        }
      }

      if(errors > 0){
        return
      }

      this.loading = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let companyId = localStorage.getItem('companyId')
      const companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/game-instances'
      let data = {
        booking_slot_id: item.id,
        game_id: item.game_id,
        start_time: this.getLocalTime(item.game_start_time),
        end_time: this.getLocalTime(item.game_end_time),
        hints: item.hasOwnProperty('hints') ? item.hints : 0,
        breaks: item.hasOwnProperty('breaks') ? item.breaks : 0,
        completed: item.hasOwnProperty('completed') ? item.completed : 1,
        is_corporate_booking: item.hasOwnProperty('is_corporate_booking') ? item.is_corporate_booking: 0,
        team_name: item.team_name,
        custom_time: toStoredCustomTime(item.custom_time) || 0,
        users: JSON.parse(item.players)
      }
      if (this.enabledFields.host && item.host_id !== null && item.host_id !== undefined) {
        data.game_master_id = item.host_id
      }
      axios.post(url,data, config)
        .then(response => {
          this.$notify({ type: 'success', message: 'Event tracker form submitted', verticalAlign: 'top', horizontalAlign: 'right' })
          if(item.email_to_group && item.photos && item.photos.length > 0){
            const photosWithIds = item.photos.filter(p => p.photo_id && p.photo_link_id)
            if (photosWithIds.length > 0) {
              this.emailToGroup(photosWithIds, item.players)
            }
          }
          if(this.incomplete_view){
            this.getIncompleteForms()
          } else {
            this.getTodaysGames()
          }


          this.loading = false
        })
        .catch(err => {
          this.loading = false
          if(err.response.status === 401){
            this.$router.push('/forbidden')
          } else if(err.response.status === 400){
            this.showAlert({ title: 'Host not available', message: 'The selected host is not available for this location. Pick another host and submit again.', variant: 'warning' })
          } else if(err.response.status === 500){
            this.showAlert({ title: 'Error', message: err.response.data, variant: 'danger' })
            if(this.incomplete_view){
              this.getIncompleteForms()
            } else {
              this.getTodaysGames()
            }
          } else {
            this.showAlert({ title: 'Error', message: 'Something went wrong', variant: 'danger' })
          }
        })
    },
    async getDefaultTracker() {
      this.loading = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/game-tracker-settings'

      axios.get(url, config)
        .then(response => {
          this.template = response.data.game_tracker_settings
          this.optional_fields = response.data.fields
          if(this.template.mode === 1){
            this.$router.push('/games/tracker')
          } else {
            // Template ready; let the guard fire the first fetch once FilterBar has
            // resolved its filters (avoids racing / double-fetching).
            this.templateReady = true
            this.maybeInitialFetch()
          }
          for(let n in this.optional_fields){
            if(this.optional_fields[n].is_enabled && !this.optional_fields[n].advanced_mode_only){
              const col = this.optional_fields[n].column_name
              this.fields.push(col === 'photo' ? { key: 'photo', label: 'Photos' } : col === 'host' ? { key: 'host', label: 'Host' } : col)
            }
            if(this.optional_fields[n].field_id === 2 && !this.optional_fields[n].is_enabled){
              this.photos_not_enabled = true
            }
            // Track enabled fields for mobile view
            const columnName = this.optional_fields[n].column_name
            const isEnabled = this.optional_fields[n].is_enabled === 1 && !this.optional_fields[n].advanced_mode_only
            if(columnName === '#_of_hints') {
              this.enabledFields.hints = isEnabled
            } else if(columnName === '#_of_breaks') {
              this.enabledFields.breaks = isEnabled
            } else if(columnName === 'completed') {
              this.enabledFields.completed = isEnabled
            } else if(columnName === 'corporate_booking') {
              this.enabledFields.corporate_booking = isEnabled
            } else if(columnName === 'custom_time') {
              this.enabledFields.custom_time = isEnabled
            } else if(columnName === 'photo') {
              this.enabledFields.photo = isEnabled
            } else if(columnName === 'team') {
              this.enabledFields.team_name = isEnabled
            } else if(columnName === 'host') {
              this.enabledFields.host = isEnabled
            }
          }
          this.fields.push('submit')
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert({ title: 'Error', message: 'Something went wrong', variant: 'danger' })
          }
        })
    },
    async getTodaysGames() {
      this.loading = true
      this.incomplete_view = false
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId +
        '/bookings?day=' + this.current + '&type=0'
      if (this.filterValues.game && this.filterValues.game.length > 0) {
        url += '&game_id=' + this.filterValues.game.join(',')
      }
      if (this.filterValues.staff && this.filterValues.staff.length > 0) {
        url += '&staff_id=' + this.filterValues.staff.join(',')
      }
      axios.get(url, config)
        .then(response => {
          let start_dt = new Date()
          let end_dt = new Date()
          for(let n in response.data.bookings){
            this.getUsersForBooking(response.data.bookings[n])
            start_dt = new Date()
            end_dt = new Date()
            start_dt.setHours(response.data.bookings[n].start_time.split(':')[0])
            start_dt.setMinutes(response.data.bookings[n].start_time.split(':')[1])
            end_dt.setHours(response.data.bookings[n].end_time.split(':')[0])
            end_dt.setMinutes(response.data.bookings[n].end_time.split(':')[1])
            response.data.bookings[n].hints = 0
            response.data.bookings[n].breaks = 0
            response.data.bookings[n].completed = 0
            response.data.bookings[n].host_id = null
            response.data.bookings[n].game_start_time = start_dt
            response.data.bookings[n].game_end_time = end_dt

            // Parse photos array (new backend) or build from flat fields (backward compat)
            let booking = response.data.bookings[n]
            if (booking.photos) {
              booking.photos = typeof booking.photos === 'string' ? JSON.parse(booking.photos) : booking.photos
            } else if (booking.uploaded_image) {
              booking.photos = [{
                photo_id: booking.photo_id,
                photo_link_id: booking.photo_link_id,
                uploaded_image: booking.uploaded_image,
                overlay_url: booking.overlay_url,
                overlay_id: booking.overlay_id,
                overlay_layers: booking.overlay_layers || null
              }]
            } else {
              booking.photos = []
            }

            // let child_slots = _.filter(response.data.bookings, {master_slot_id:  response.data.bookings[n].id})
            //
            // if(child_slots.length > 0){
            //   let child_waivers = []
            //   for(let m in child_slots){
            //     if(child_slots[m].players){
            //       child_waivers = child_waivers.concat(JSON.parse(child_slots[m].players))
            //     }
            //   }
            //
            //   if(response.data.bookings[n].players === null){
            //     response.data.bookings[n].players = JSON.stringify(child_waivers)
            //   } else {
            //     response.data.bookings[n].players = JSON.stringify(JSON.parse(response.data.bookings[n].players).concat(child_waivers))
            //   }
            // }

          }
          if(this.fields.includes('booking_time')){
            this.fields.splice(0,1)
            this.fields.splice(0, 0, 'time')
          }
          this.game_instances = response.data.bookings
          this.applyHostDefaults()
          this.loading = false
        })
        .catch(err => {
          // this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert({ title: 'Error', message: 'Something went wrong', variant: 'danger' })
          }
        })
    },
    async getIncompleteForms() {
      this.loading = true
      this.incomplete_view = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId +
        '/bookings?type=4'
      if (this.filterValues.game && this.filterValues.game.length > 0) {
        url += '&game_id=' + this.filterValues.game.join(',')
      }
      if (this.filterValues.staff && this.filterValues.staff.length > 0) {
        url += '&staff_id=' + this.filterValues.staff.join(',')
      }
      axios.get(url, config)
        .then(response => {
          let start_dt = new Date()
          let end_dt = new Date()
          for(let n in response.data.bookings){
            if(response.data.bookings[n].start_time != null){
              start_dt.setHours(response.data.bookings[n].start_time.split(':')[0])
              start_dt.setMinutes(response.data.bookings[n].start_time.split(':')[1])
            }
            if(response.data.bookings[n].end_time != null) {
              end_dt.setHours(response.data.bookings[n].end_time.split(':')[0])
              end_dt.setMinutes(response.data.bookings[n].end_time.split(':')[1])
            }

            response.data.bookings[n].hints = 0
            response.data.bookings[n].breaks = 0
            response.data.bookings[n].completed = 0
            response.data.bookings[n].host_id = null
            response.data.bookings[n].game_start_time = start_dt
            response.data.bookings[n].game_end_time = end_dt

            // Parse photos array (new backend) or build from flat fields (backward compat)
            let booking = response.data.bookings[n]
            if (booking.photos) {
              booking.photos = typeof booking.photos === 'string' ? JSON.parse(booking.photos) : booking.photos
            } else if (booking.uploaded_image) {
              booking.photos = [{
                photo_id: booking.photo_id,
                photo_link_id: booking.photo_link_id,
                uploaded_image: booking.uploaded_image,
                overlay_url: booking.overlay_url,
                overlay_id: booking.overlay_id,
                overlay_layers: booking.overlay_layers || null
              }]
            } else {
              booking.photos = []
            }

          }
          this.fields.splice(0, 1, )
          this.fields.splice(0, 0, 'booking_time')
          this.game_instances = response.data.bookings
          this.applyHostDefaults()
          this.loading = false
        })
        .catch(err => {
          // this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert({ title: 'Error', message: 'Something went wrong', variant: 'danger' })
          }
        })
    },
    async getSignedURL (file) {
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let url = API_LOCATION + 'upload/photos'

      let payload = {
        filePath: file.name,
        contentType: file.type
      }

      return new Promise ( function(resolve, reject) {
        axios.post(url, payload, config)
          .then((res) => {
            resolve(res.data)
          })
          .catch((err) => {
            console.error(err)
            reject(err)
          })
      })
    },
    uploadFile (file, signedUrl) {
      let config = { headers: { 'Content-Type': file.type } }
      return new Promise ( function(resolve, reject) {
        axios.put(signedUrl, file, config)
          .then((res) => resolve(res))
          .catch((err) => {
            console.error(err)
            reject(err)
          })
      })
    },
    async takePhoto(item, event){
      let file = event.target.files[0]
      if (!file) return
      if (!file.type.match('image.*')) {
        this.showAlert({ title: 'Error', message: 'You must upload an image', variant: 'danger' })
        return
      }

      if (this.review_before_save) {
        // findMatchingOverlay loads the photo to pick the closest aspect ratio, so it gets the object URL.
        const url = URL.createObjectURL(file)
        const match = this.overlays.length > 0 ? await this.findMatchingOverlay(item.game_id, item.completed, url) : null
        this.pendingPhoto = { url, file, item, inputEl: event.target, overlayId: match ? match.id : null }
        return
      }
      await this.uploadPhoto(item, file)
    },
    // Live overlay camera ---------------------------------------------------
    async onTileClick(item) {
      // With the system camera the file input inside the tile handles the click.
      if (!this.useLiveCamera) return
      const match = this.overlays.length > 0 ? await this.findMatchingOverlay(item.game_id, item.completed, '') : null
      this.liveCamera = { show: true, item, overlayId: match ? match.id : null }
    },
    closeLiveCamera() {
      this.liveCamera.show = false
    },
    useNativeCamera() {
      this.closeLiveCamera()
      this.$nextTick(() => this.$refs.nativeInput && this.$refs.nativeInput.click())
    },
    async onLiveCapture({ file, overlayId }) {
      const item = this.liveCamera.item
      this.closeLiveCamera()
      if (!item) return
      const overlay = overlayId ? this.overlays.find(o => o.id === overlayId) || null : null
      if (this.review_before_save) {
        this.pendingPhoto = { url: URL.createObjectURL(file), file, item, inputEl: null, overlayId: overlay ? overlay.id : null, source: 'live' }
        return
      }
      // The live view already showed the overlay, so the choice made there stands in for auto apply.
      await this.uploadPhoto(item, file, { overlay, overlayLayers: null })
    },
    // Review drawer (pending mode) -------------------------------------------
    cancelPending() {
      if (!this.pendingPhoto) return
      URL.revokeObjectURL(this.pendingPhoto.url)
      if (this.pendingPhoto.inputEl) this.pendingPhoto.inputEl.value = ''
      this.pendingPhoto = null
    },
    retakePending() {
      const pending = this.pendingPhoto
      this.cancelPending()
      if (!pending) return
      if (pending.source === 'live') {
        this.liveCamera = { show: true, item: pending.item, overlayId: pending.overlayId || null }
      } else if (pending.inputEl) {
        pending.inputEl.click()
      }
    },
    async usePending({ overlay, overlayLayers }) {
      const pending = this.pendingPhoto
      if (!pending) return
      this.pendingPhoto = null
      await this.uploadPhoto(pending.item, pending.file, { overlay, overlayLayers })
      URL.revokeObjectURL(pending.url)
      if (pending.inputEl) pending.inputEl.value = ''
    },
    // review: { overlay, overlayLayers } from the review drawer; when present it
    // replaces auto apply, because staff already decided in the review.
    async uploadPhoto(item, file, review = null){
      // Capture values immediately before any async operations
      const bookingSlotId = item.id
      const companyId = localStorage.getItem('companyId')
      const groupId = JSON.parse(localStorage.getItem('group')).id
      const t = new Date().toLocaleString('sv')

      // Create photo object and push to array
      const photo = {
        uploaded_image: URL.createObjectURL(file),
        photo_id: null,
        photo_link_id: null,
        overlay_url: '',
        overlay_id: null,
        overlay_layers: null,
        isProcessingOverlay: true
      }
      if (!item.photos) this.$set(item, 'photos', [])
      item.photos.push(photo)
      this.img_key++

      // Upload to S3
      let signedUrl
      try {
        signedUrl = await this.getSignedURL(file)
        await this.uploadFile(file, signedUrl)
      } catch (err) {
        const idx = item.photos.indexOf(photo)
        if (idx > -1) item.photos.splice(idx, 1)
        this.showAlert({ title: 'Error', message: 'Failed to upload photo. Please try again.', variant: 'danger' })
        return
      }
      if (!signedUrl) {
        const idx = item.photos.indexOf(photo)
        if (idx > -1) item.photos.splice(idx, 1)
        return
      }

      const s3Url = signedUrl.split("?")[0]

      // Create player_photos record
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + groupId + '/photos?bsId=' + bookingSlotId + '&t=' + t

      let data = {
        uploaded_image: s3Url,
        aspect_ratio: this.default_aspect_ratio
      }

      try {
        const response = await axios.post(url, data, config)
        this.$set(photo, 'uploaded_image', s3Url)
        photo.photo_link_id = response.data.photo_link_id
        photo.photo_id = response.data.photo_id

        // Auto-apply overlay if conditions match
        if (review) {
          if (review.overlay) {
            // processOverlay drops an adjustment that belongs to another overlay, so the id moves first.
            this.$set(photo, 'overlay_id', review.overlay.id)
            this.$set(photo, 'overlay_layers', review.overlayLayers || null)
            await this.processOverlay(item, photo, review.overlay)
          } else {
            this.$set(photo, 'isProcessingOverlay', false)
          }
        } else if (this.overlays.length > 0) {
          await this.autoApplyOverlay(item, photo)
        } else {
          this.$set(photo, 'isProcessingOverlay', false)
        }
      } catch (err) {
        console.log(err)
        this.$set(photo, 'isProcessingOverlay', false)
        if (err.response && err.response.status == 401) {
          this.$router.push('/forbidden')
        } else {
          this.showAlert({
            title: 'Something went wrong!',
            variant: 'danger',
            confirmText: 'Try again'
          })
        }
      }
    },
    async deletePhoto(item, photoIndex) {
      const result = await this.showAlert({
        title: 'Delete photo?',
        message: 'This action cannot be undone.',
        variant: 'warning',
        showCancel: true,
        confirmText: 'Delete'
      })

      if (result !== true) return

      const photo = item.photos[photoIndex]
      if (photo && photo.photo_id) {
        try {
          let token = await this.getAuthToken()
          let config = {headers: {Authorization: token}}
          let companyId = localStorage.getItem('companyId')
          let groupId = JSON.parse(localStorage.getItem('group')).id
          let url = API_LOCATION + 'companies/' + companyId + '/groups/' + groupId + '/photos/' + photo.photo_id

          await axios.delete(url, config)
        } catch (err) {
          console.log(err)
          if (err.response && err.response.status === 401) {
            this.$router.push('/forbidden')
            return
          }
          this.showAlert({ title: 'Error', message: 'Failed to delete photo', variant: 'danger' })
          return
        }
      }

      item.photos.splice(photoIndex, 1)
      // Close lightbox if the deleted photo was being viewed
      if (this.showLightbox && this.lightboxItem === item) {
        if (item.photos.length === 0) {
          this.closeLightbox()
        } else if (this.lightboxPhotoIndex >= item.photos.length) {
          this.lightboxPhotoIndex = item.photos.length - 1
        }
      }
      this.img_key++
    },
    // Overlay methods
    parseAspectRatio(ratioString) {
      if (!ratioString) return null
      const parts = ratioString.split(':')
      if (parts.length !== 2) return null
      return parseFloat(parts[0]) / parseFloat(parts[1])
    },
    async findMatchingOverlay(gameId, completed, photoUrl) {
      const matchingOverlays = this.overlays.filter(overlay => {
        if (!overlay.applied_by_default) return false

        let gameDefaults = overlay.game_defaults
        if (typeof gameDefaults === 'string') {
          gameDefaults = JSON.parse(gameDefaults)
        }
        if (!gameDefaults || gameDefaults.length === 0) return false

        const gameDefault = gameDefaults.find(gd => gd.game_id === gameId)
        if (!gameDefault) return false

        const condition = gameDefault.apply_condition
        const isCompleted = completed === true || completed === 1
        const isNotCompleted = completed === false || completed === 0

        if (condition === 'always') return true
        if (condition === 'escaped' && isCompleted) return true
        if (condition === 'not_escaped' && isNotCompleted) return true
        if (completed === null || completed === undefined) {
          return condition === 'always'
        }

        return false
      })

      if (matchingOverlays.length === 0) return null
      if (matchingOverlays.length === 1) return matchingOverlays[0]

      // Multiple matches - prefer closest aspect ratio
      if (!photoUrl) return matchingOverlays[0]
      try {
        const img = await this.loadImage(photoUrl)
        const photoRatio = img.naturalWidth / img.naturalHeight

        let bestMatch = matchingOverlays[0]
        let smallestDiff = Infinity

        for (const overlay of matchingOverlays) {
          const overlayRatio = this.parseAspectRatio(overlay.aspect_ratio)
          if (overlayRatio !== null) {
            const diff = Math.abs(photoRatio - overlayRatio)
            if (diff < smallestDiff) {
              smallestDiff = diff
              bestMatch = overlay
            }
          }
        }

        return bestMatch
      } catch (err) {
        console.error('Could not load image for aspect ratio detection:', err)
        return matchingOverlays[0]
      }
    },
    async autoApplyOverlay(item, photo) {
      const matchingOverlay = await this.findMatchingOverlay(item.game_id, item.completed, photo.uploaded_image)
      if (!matchingOverlay) {
        this.$set(photo, 'isProcessingOverlay', false)
        return
      }
      if (photo.overlay_id) {
        this.$set(photo, 'isProcessingOverlay', false)
        return
      }

      // Set the overlay and apply it
      this.selectedItem = item
      this.selectedPhoto = photo
      this.modalSelectedOverlay = {
        id: matchingOverlay.id,
        name: matchingOverlay.name,
        layers: matchingOverlay.layers,
        aspect_ratio: matchingOverlay.aspect_ratio,
        reference_height: matchingOverlay.reference_height,
        reference_width: matchingOverlay.reference_width,
        crop_to_overlay: matchingOverlay.crop_to_overlay
      }
      await this.applyOverlayFromModal(true)
    },
    async getOverlays() {
      try {
        let token = await this.getAuthToken()
        let config = {headers: {Authorization: token}}
        let companyId = localStorage.getItem('companyId')
        let groupId = JSON.parse(localStorage.getItem('group')).id
        let url = API_LOCATION + 'companies/' + companyId + '/groups/' + groupId + '/photo-overlays'

        const response = await axios.get(url, config)
        this.overlays = response.data.overlays
        this.review_before_save = response.data.review_before_save === true
        this.camera_mode = response.data.camera_mode === 'live' ? 'live' : 'native'
      } catch (err) {
        console.log('Failed to load overlays:', err)
      }
    },
    loadImage(url) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.crossOrigin = 'Anonymous'
        img.onload = () => resolve(img)
        img.onerror = () => reject(new Error(`Failed to load image at ${url}`))
        img.src = url
      })
    },
    // Customer facing event name for photo output. game_name on the booking row is the
    // staff facing internal name; game_customer_name is games.name.
    eventNameFor(item) {
      return (item && (item.game_customer_name || item.game_name)) || ''
    },
    async processDynamicContent(content, item) {
      let group = JSON.parse(localStorage.getItem('group'))
      let company = JSON.parse(localStorage.getItem('company'))

      if (content.includes('[Team Name]')) {
        content = content.replaceAll('[Team Name]', item.team_name || '')
      }

      if (content.includes('[Company Name]')) {
        content = content.replaceAll('[Company Name]', company?.name || '')
      }

      if (content.includes('[Location Name]')) {
        content = content.replaceAll('[Location Name]', group?.name || '')
      }

      if (content.includes('[Event Name]') || content.includes('[Game Name]')) {
        // [Game Name] is the legacy spelling of [Event Name]; overlays saved before the rename still carry it.
        const eventName = this.eventNameFor(item)
        content = content.replaceAll('[Event Name]', eventName).replaceAll('[Game Name]', eventName)
      }

      if (content.includes('[Completion Time]')) {
        if (item.game_start_time && item.game_end_time) {
          const diffMs = item.game_end_time - item.game_start_time
          const diffMins = Math.floor(diffMs / 60000)
          content = content.replaceAll('[Completion Time]', diffMins + ' minutes')
        } else {
          content = content.replaceAll('[Completion Time]', '')
        }
      }

      if (content.includes('[Custom Time]')) {
        content = content.replaceAll('[Custom Time]', formatCustomTime(item.custom_time))
      }

      if (content.includes('[Time Remaining]')) {
        if (item.game_start_time && item.game_end_time && item.duration_minutes) {
          const diffMs = item.game_end_time - item.game_start_time
          const diffMins = Math.floor(diffMs / 60000)
          const remaining = Math.max(0, item.duration_minutes - diffMins)
          content = content.replaceAll('[Time Remaining]', remaining + ' minutes')
        } else {
          content = content.replaceAll('[Time Remaining]', '')
        }
      }

      if (content.includes('[Number Of Hints]')) {
        content = content.replaceAll('[Number Of Hints]', (item.hints || 0) + ' hints')
      }

      return content
    },
    // Modal methods
    applyOverlayFromModal(isAutoApply = false) {
      if (!this.modalSelectedOverlay || !this.selectedPhoto) return

      const item = this.selectedItem
      const photo = this.selectedPhoto
      const selectedOverlay = this.modalSelectedOverlay

      // Close modal immediately and show localized loader on the photo
      this.showOverlayModal = false
      this.previewDataUrl = ''
      this.$set(photo, 'isProcessingOverlay', true)

      // Defer processing to allow modal to close first
      setTimeout(() => {
        this.processOverlay(item, photo, selectedOverlay, isAutoApply)
      }, 100)
    },
    async processOverlay(item, photo, selectedOverlay) {
      try {
        // Load the base image (use original, not existing overlay)
        const baseImage = await this.loadImage(photo.uploaded_image)
        // An overlay with crop on keeps only the centered area with its aspect ratio.
        const { canvas: compositeCanvas, ctx, width: photoWidth, height: photoHeight } = preparePhotoCanvas(baseImage, {
          aspectRatio: selectedOverlay.aspect_ratio,
          crop: cropsToOverlay(selectedOverlay),
        })

        // A per photo adjustment belongs to one overlay; a different overlay starts from its template.
        if (photo.overlay_layers && photo.overlay_id !== selectedOverlay.id) {
          this.$set(photo, 'overlay_layers', null)
        }
        const eff = effectiveLayers(selectedOverlay, photo.overlay_layers || null)

        // Shared renderer: uniform fit, centered, same code the editor previews with.
        await bakeOverlay({
          ctx,
          layers: eff.layers,
          aspectRatio: selectedOverlay.aspect_ratio,
          referenceWidth: eff.referenceWidth,
          referenceHeight: eff.referenceHeight,
          photoWidth,
          photoHeight,
          resolveText: (content) => this.processDynamicContent(content, item),
        })

        // Convert canvas to blob and upload
        compositeCanvas.toBlob(async (blob) => {
          try {
            const file = new File([blob], 'composite_overlay.jpg', { type: 'image/jpeg' })

            const signedUrl = await this.getSignedURL(file)
            await this.uploadFile(file, signedUrl)
            if (!signedUrl) {
              this.showAlert({ title: 'Error', message: 'Failed to upload photo. Please try again.', variant: 'danger' })
              return
            }

            const s3Url = signedUrl.split('?')[0]
            let token = await this.getAuthToken()
            let config = {headers: {Authorization: token}}
            let companyId = localStorage.getItem('companyId')
            let groupId = JSON.parse(localStorage.getItem('group')).id

            if (photo.photo_id) {
              let url = API_LOCATION + 'companies/' + companyId + '/groups/' + groupId + '/photos/' + photo.photo_id + '/overlay'

              let data = {
                overlay_url: s3Url,
                overlay_id: selectedOverlay.id,
                overlay_layers: photo.overlay_layers || null
              }

              await axios.post(url, data, config)
            }

            this.$set(photo, 'overlay_url', s3Url)
            this.$set(photo, 'overlay_id', selectedOverlay.id)
            this.img_key++
            this.$set(photo, 'isProcessingOverlay', false)
          } catch (err) {
            console.log(err)
            this.$set(photo, 'isProcessingOverlay', false)
            this.showAlert({ title: 'Error', message: 'Failed to save overlay', variant: 'danger' })
          }
        }, 'image/jpeg', OVERLAY_EXPORT_QUALITY)
      } catch (error) {
        console.error('Error in applyOverlay:', error)
        this.$set(photo, 'isProcessingOverlay', false)
        this.showAlert({ title: 'Error', message: 'Failed to create overlay', variant: 'danger' })
      }
    },
    async clearOverlayFromModal() {
      if (!this.selectedPhoto) return

      this.isProcessingOverlay = true
      const photo = this.selectedPhoto

      try {
        if (photo.photo_id) {
          let token = await this.getAuthToken()
          let config = {headers: {Authorization: token}}
          let companyId = localStorage.getItem('companyId')
          let groupId = JSON.parse(localStorage.getItem('group')).id
          let url = API_LOCATION + 'companies/' + companyId + '/groups/' + groupId + '/photos/' + photo.photo_id + '/overlay'

          let data = {
            overlay_url: '',
            overlay_id: null
          }

          await axios.post(url, data, config)
        }

        this.$set(photo, 'overlay_url', '')
        this.$set(photo, 'overlay_id', null)
        this.$set(photo, 'overlay_layers', null)
        this.modalSelectedOverlay = ''
        this.previewDataUrl = ''
        this.img_key++
        this.isProcessingOverlay = false
        this.showOverlayModal = false
      } catch (err) {
        console.log(err)
        this.isProcessingOverlay = false
        this.showAlert({ title: 'Error', message: 'Failed to clear overlay', variant: 'danger' })
      }
    },
    openLightbox(item, photoIndex) {
      this.lightboxItem = item
      this.lightboxPhotoIndex = photoIndex
      this.selectedItem = item
      this.selectedPhoto = item.photos[photoIndex]
      this.showLightbox = true
    },
    closeLightbox() {
      this.showLightbox = false
      this.lightboxItem = null
      this.lightboxPhotoIndex = 0
    },
    onLightboxDelete(photoIndex) {
      this.deletePhoto(this.lightboxItem, photoIndex)
    },
    async onLightboxClearOverlay(photoIndex) {
      const photo = this.lightboxItem.photos[photoIndex]
      if (photo.overlay_layers) {
        const ok = await this.showAlert({
          title: 'Discard adjustments?',
          message: 'This photo has adjusted layers. Clearing the overlay removes them.',
          variant: 'warning',
          showCancel: true,
          confirmText: 'Clear',
          confirmVariant: 'danger',
        })
        if (ok !== true) return
      }
      this.selectedItem = this.lightboxItem
      this.selectedPhoto = photo
      this.clearOverlayFromModal()
    },
    // Apply from the drawer: whatever is on its stage (a newly picked overlay, an
    // adjustment, or both) is baked onto that photo.
    async onLightboxApply({ overlayLayers, photoIndex, overlay }) {
      const item = this.lightboxItem
      const photo = item && item.photos[photoIndex]
      if (!item || !photo || !overlay) return
      // processOverlay drops an adjustment that belongs to another overlay, so
      // the photo's overlay id moves to the one the drawer baked for first.
      this.$set(photo, 'overlay_id', overlay.id)
      this.$set(photo, 'overlay_layers', overlayLayers)
      this.closeLightbox()
      this.selectedItem = item
      this.selectedPhoto = photo
      this.$set(photo, 'isProcessingOverlay', true)
      await this.processOverlay(item, photo, overlay)
    },
  },
  created() {
    // Seed the date navigator + FilterBar's flat values from the URL BEFORE child
    // components mount, so <filter-bar> reads the URL-derived initial-value.
    this.setToday()
    this.initFromQueryParameters()
  },
  mounted() {
    if(!(_.find(JSON.parse(localStorage.getItem('features')), {id: 5}))){
      this.photos_not_enabled = true
    } else {
      this.photos_not_enabled = false
      this.getOverlays()
    }
    this.date_format_cal = this.date_format
    this.date_format_cal =  this.date_format_cal.replaceAll('Y', 'y')
    this.date_format_cal = this.date_format_cal.replaceAll('D', 'd')
    if(JSON.parse(localStorage.getItem('group')).show_day_of_week){
      this.day_of_week = 'ddd, '
    }
    if(this.time_format === 'HH:mm'){
      this.picker_time_format = 'HH:mm'
    }
    this.getGames()
    this.getStaffMembers()
    this.getDefaultTracker()
  }

}
</script>

<style scoped>
.photo-thumbnails-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}
.photo-thumb-wrapper {
  position: relative;
  display: inline-block;
}
.photo-thumb-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  color: white;
}
.photo-add-tile {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed rgba(225, 78, 202, 0.4);
  border-radius: 4px;
  color: #e14eca;
  cursor: pointer;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
}
.photo-add-tile:hover {
  border-color: #e14eca;
  background: rgba(225, 78, 202, 0.1);
}
/* Incomplete view adds a date to the Booking column, so the table needs more room: cards until 1400px */
.tracker-table--incomplete {
  display: none !important;
}
@media (min-width: 1400px) {
  .tracker-cards--incomplete {
    display: none !important;
  }
  .tracker-table--incomplete {
    display: block !important;
  }
}
.tracker-title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem 1rem;
  margin-bottom: 0.75rem;
}
.tracker-title-row__nav {
  margin-left: auto;
}
@media (max-width: 575.98px) {
  .tracker-title-row__nav {
    width: 100%;
  }
}
/* Mobile and tablet booking card */
.tracker-card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(9rem, 1fr));
  column-gap: 1rem;
}
/* Team name and host get room for full names: full width on phones,
   two columns once the card is wide enough to hold several per line */
.tracker-card-grid__wide {
  grid-column: 1 / -1;
}
@media (min-width: 576px) {
  .tracker-card-grid__wide {
    grid-column: span 2;
  }
}
.tracker-card-toggles {
  display: flex;
  flex-wrap: wrap;
  column-gap: 2rem;
  row-gap: 0.25rem;
  margin-top: 0.5rem;
}
.tracker-card-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  column-gap: 1rem;
  margin-bottom: 0.5rem;
}
.tracker-card-submit {
  width: 100%;
  margin: 0.5rem 0 0;
}
@media (min-width: 576px) {
  .tracker-card-submit {
    width: auto;
    min-width: 9rem;
    margin-left: auto;
  }
}
/* Desktop tracker table: the font scales with the table's own width and every
   column is sized in em, so the whole row shrinks together and stays on one line.
   Below the 11px floor the table scrolls sideways instead. */
.tracker-fit {
  container-type: inline-size;
}
.tracker-fit ::v-deep table {
  font-size: 14px;
  font-size: clamp(11px, 0.9cqi, 14px);
}
.tracker-fit ::v-deep th,
.tracker-fit ::v-deep td {
  padding: 0.6em 0.4em;
  vertical-align: middle;
  white-space: nowrap;
}
.tracker-fit ::v-deep th {
  font-size: max(10px, 0.85em);
}
.tracker-fit ::v-deep label.error {
  white-space: normal;
  font-size: 0.85em;
  line-height: 1.2;
  margin: 0;
}
.tracker-fit ::v-deep .form-group {
  margin-bottom: 0;
}
/* Element hard-codes 14px on these wrappers; inherit so every field, picker
   and switch sizes off the same scaled table font as the plain inputs */
.tracker-fit ::v-deep .el-input,
.tracker-fit ::v-deep .el-select,
.tracker-fit ::v-deep .el-date-editor,
.tracker-fit ::v-deep .el-switch {
  font-size: inherit;
}
.tracker-fit ::v-deep td label:not(.error) {
  font-size: 0.85em;
}
.tracker-fit ::v-deep td .btn-link.like {
  font-size: 1em;
}
.tracker-fit ::v-deep .form-control,
.tracker-fit ::v-deep .el-input__inner {
  /* Field text runs a step below the cell text; 3.05em keeps the field
     2.6 cell ems tall. Placeholders inherit the same size. */
  font-size: 0.85em;
  height: 3.05em;
  line-height: 3.05em;
  padding: 0 0.6em;
}
/* The theme forces 30px on date editors with !important */
.tracker-fit ::v-deep .el-date-editor.el-input .el-input__inner {
  padding-left: 2.2em !important;
}
.tracker-fit ::v-deep .el-input--suffix .el-input__inner {
  padding-right: 2.8em;
}
.tracker-fit ::v-deep .el-input__prefix,
.tracker-fit ::v-deep .el-input__suffix {
  display: flex;
  align-items: center;
}
.tracker-fit ::v-deep .el-input__icon {
  line-height: 1;
  width: 1.6em;
}
.tracker-fit ::v-deep .el-date-editor.el-input,
.tracker-fit ::v-deep .el-select {
  width: 100%;
}
/* Minimums set the one-line fit; spare table width goes into the fields */
/* Switches scale with the row: 40x20px at 14px. Element sets the width inline. */
.tracker-fit ::v-deep .el-switch {
  height: 1.43em;
  line-height: 1.43em;
}
.tracker-fit ::v-deep .el-switch__core {
  width: 2.85em !important;
  height: 1.43em;
  border-radius: 0.72em;
}
.tracker-fit ::v-deep .el-switch__core:after {
  width: calc(1.43em - 4px);
  height: calc(1.43em - 4px);
}
.tracker-fit ::v-deep .el-switch.is-checked .el-switch__core:after {
  margin-left: calc(-1.43em + 3px);
}
.tracker-fit__w-time { min-width: 7em; max-width: 9em; width: 100%; }
.tracker-fit__w-custom { min-width: 5em; max-width: 7em; width: 100%; }
.tracker-fit__w-host { min-width: 8.5em; width: 100%; }
.tracker-fit__w-team { min-width: 7em; width: 100%; }
.tracker-fit__w-count { min-width: 3.5em; max-width: 5em; width: 100%; }
.tracker-fit__w-booking { white-space: nowrap; }
.tracker-fit__booking-time { font-size: 0.9em; }
.tracker-fit__event {
  display: flex;
  flex-direction: column;
  max-width: 10em;
}
.tracker-fit__event-name {
  font-size: 1.1em;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tracker-fit__staff {
  font-size: max(10px, 0.8em);
  overflow: hidden;
  text-overflow: ellipsis;
}
.tracker-fit__photos {
  flex-wrap: nowrap;
  gap: 0.35em;
}
.tracker-fit__photos img,
.tracker-fit__photos .photo-add-tile {
  width: 2.6em;
  height: 2.6em;
}
.tracker-fit__more {
  font-weight: 600;
}
.tracker-fit__submit {
  display: inline-block;
  font-size: 1.25em;
}
.photo-add-tile input[type="file"] {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}
</style>

